import { addToCart } from 'analytics/snowplow/events'
import AccountAccessModal, { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal'
import TextButton from 'LuxKit/Button/TextButton'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { FlightsClassTypes } from 'constants/flight'
import ModalContext from 'contexts/ModalContext'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { generateMultipleRoomCheckoutItems } from 'lib/checkout/accommodation/cart'
import { generateAccommodationBundleFlightItems } from 'lib/checkout/flights/cart'
import { isRoomsInvalid } from 'lib/search/searchUtils'
import React, { useContext, useMemo } from 'react'
import * as Analytics from 'analytics/analytics'
import { PackageOptionViewByRoom, LoadingStatusByRoom, PackagesOptionsViewsByRoom, getCalendarsToBeFetchedByRoom, getDurationPackageOptionsByRoom, getIsLoadingByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { selectLoggedIn } from 'selectors/accountSelectors'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import moment from 'moment'
import Group from 'components/utils/Group'
import useCharterFlightJourneysForHotelDates from 'hooks/CharterFlights/useCharterFlightJourneysForHotelDates'
import { addToUserCart } from 'actions/UserCartActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { selectCartOffers } from 'selectors/userCart'
import BodyText from 'LuxKit/Typography/BodyText'

interface Props extends React.ComponentProps<typeof TextButton> {
  rooms: Array<App.RoomOccupants>
  offer: App.HotelOffer | App.HotelOfferSummary | App.VillaOffer | App.VillaOfferSummary
  checkIn?: string
  checkOut?: string
  duration?: number
  activePackagesByRoom: PackageOptionViewByRoom
  isLoadingByRoom: LoadingStatusByRoom
  calendarsToBeFetchedByRoom: LoadingStatusByRoom
  durationPackagesByRoom: PackagesOptionsViewsByRoom
  loggedIn?: boolean
  currencyCode: string
  isMultiRoomConnected: boolean
  airport?: App.Airport
  disabled?: boolean
  onProceedToBooking: (items: Array<App.Checkout.AnyItem>, connectRooms: boolean, keepBrowsing: boolean) => void
  flightPrice?: number
  flightsSelected: boolean
  charterBundleRoute?: App.CharterBundleRoute
  fareClass: FlightsClassTypes
}

function MultiRoomOfferCheckoutButton(props: Props) {
  const {
    rooms,
    offer,
    checkIn,
    checkOut,
    duration,
    activePackagesByRoom,
    isLoadingByRoom,
    calendarsToBeFetchedByRoom,
    durationPackagesByRoom,
    loggedIn,
    currencyCode,
    isMultiRoomConnected,
    airport,
    disabled,
    children,
    onProceedToBooking,
    flightPrice,
    flightsSelected,
    charterBundleRoute,
    fareClass,
    ...rest
  } = props

  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  const offers = useAppSelector(selectCartOffers)
  const offerPageDispatch = useOfferPageDispatch()
  const packagesByRoom = useMemo(() => {
    const selectedPackages = { ...activePackagesByRoom }
    Object
      .entries(selectedPackages)
      // filters only rooms without package
      .filter(([, selectedPackage]) => !selectedPackage)
      .forEach(([roomId]) => {
        const isThisRoomLoading = isLoadingByRoom[roomId] || calendarsToBeFetchedByRoom[roomId]
        // If this room has a "loading" state, we select
        // the first available package for it, only for the
        // component to show the loading indicator
        selectedPackages[roomId] = isThisRoomLoading ? durationPackagesByRoom[roomId][0] : undefined
      })

    return selectedPackages
  }, [activePackagesByRoom, isLoadingByRoom, durationPackagesByRoom, calendarsToBeFetchedByRoom])

  const {
    departingJourney: departingCharterFlightJourney,
    returningJourney: returningCharterFlightJourney,
    departingFareFamily: departingCharterFareFamily,
    returningFareFamily: returningCharterFareFamily,
    fetching: charterFlightsFetching,
    searchResultsTimestamp: charterFlightSearchResultsTimestamp,
    providerSearchId: charterFlightSearchId,
    hasAllFareData: hasAllCharterFlightData,
  } = useCharterFlightJourneysForHotelDates({
    charterBundleRoute,
    checkInDate: checkIn,
    checkOutDate: checkOut,
    occupancies: rooms,
    fareClass,
  })

  const awaitingCharterBundleData = !!charterBundleRoute && charterFlightsFetching
  const failedToFetchCharterBundleData = !!charterBundleRoute && !awaitingCharterBundleData && !hasAllCharterFlightData

  const proceedToBook = (keepBrowsing: boolean) => {
    if (!checkIn) return
    const checkOut = moment(checkIn).add(duration, 'days').format(ISO_DATE_FORMAT)
    const dates = { checkIn, checkOut }

    const accommodationItems = generateMultipleRoomCheckoutItems(packagesByRoom, offer, dates, rooms, currencyCode)
    const flightItems = flightsSelected && airport ? generateAccommodationBundleFlightItems({
      rooms,
      airport,
      offer,
      accommodationItems,
      quotedFare: flightPrice,
      charterBundleRoute,
      fareClass,
      departingCharterFlightJourney,
      returningCharterFlightJourney,
      departingCharterFareFamily,
      returningCharterFareFamily,
      charterFlightSearchResultsTimestamp,
      charterFlightSearchId,
    }) : []

    Analytics.trackEvent(addToCart())

    if (charterBundleRoute && flightItems.length > 0) {
      const flightItemsReadyToAdd = flightItems.filter(item => item.flights.length > 0)
      const accommodationItemsAlreadyInCart = accommodationItems.filter(item => offers?.[item.offerId])

      // add to cart, if item is not already in the cart..
      if (accommodationItemsAlreadyInCart.length === 0) {
        dispatch(addToUserCart([...accommodationItems, ...flightItemsReadyToAdd], true, false))
      }
    }

    onProceedToBooking([...accommodationItems, ...flightItems], isMultiRoomConnected, keepBrowsing)
  }

  const onBookNowClick = async(e: React.MouseEvent<HTMLButtonElement>, keepBrowsing: boolean = false) => {
    if (!checkIn) {
      e.preventDefault()

      offerPageDispatch({ type: OfferPageActions.setRoomType, room: undefined })
      offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
    } else if (!loggedIn) {
      e.preventDefault()

      const result = await showModal<AccountAccessModalResult>(<AccountAccessModal />)

      if (result.loggedIn && !isRoomsInvalid(rooms)) {
        proceedToBook(keepBrowsing)
      }
    } else if (!isRoomsInvalid(rooms)) {
      proceedToBook(keepBrowsing)
    }
  }

  if (failedToFetchCharterBundleData) {
    return <BodyText variant="medium" colour="critical">Failed to fetch flight data</BodyText>
  }

  return <Group direction="horizontal" gap={8}>
    <TextButton
      {...rest}
      type="submit"
      onClick={onBookNowClick}
      disabled={disabled || awaitingCharterBundleData}
    >
      {children}
    </TextButton>
  </Group>
}

export default withOfferPageAndReduxState((state, appState) => ({
  currencyCode: appState.geo.currentCurrency,
  checkIn: state.checkInDate,
  checkOut: state.checkOutDate,
  duration: state.duration,
  rooms: state.rooms,
  offer: state.offer,
  isMultiRoomConnected: state.isMultiRoomConnected,
  isLoadingByRoom: getIsLoadingByRoom(state, appState),
  calendarsToBeFetchedByRoom: getCalendarsToBeFetchedByRoom(state, appState),
  durationPackagesByRoom: getDurationPackageOptionsByRoom(state, appState),
  loggedIn: selectLoggedIn(appState),
  airport: state.airport,
  flightsSelected: state.flights,
  charterBundleRoute: state.charterBundleRoute,
  fareClass: state.fareClass,
}), MultiRoomOfferCheckoutButton)
